import { ProfilePage } from "components/ProfilePage"
import * as Civility from "enums/Civility"
import * as UserType from "enums/UserType"
import * as CertificationLevelType from "enums/CertificationLevelType"
import * as NavigationHobby from "enums/NavigationHobby"
import * as BudgetOwnerSharing from "enums/BudgetOwnerSharing"
import { Text } from "components/Text"
import { Tag } from "components/Tag"
import { DescriptionsList, DescriptionItem } from "components/DescriptionsList"
import {
  Accordion,
  AccordionContent,
  AccordionSummary,
} from "components/Accordion"
import { FFVLevelIcon } from "components/FFVLevelIcon"
import { useTranslator } from "components/Translator"
import { NestableList } from "components/NestableList"
import { ValueWithUnit } from "components/ValueWithUnit"
import { RawLink, Link } from "components/Link"
import { formatPrice } from "components/Price"
import { MinusCircle, Pen, PlusCircle } from "components/Icon"
import { QuestionCircle } from "components/Icon/QuestionCircle"
import { Separator } from "components/Separator"
import { DynamicIcon } from "components/Icon/DynamicIcon"
import partition from "lodash/partition"
import { useConnectedUser } from "components/ConnectedUserProvider"
import { Button } from "components/Button"
import { useRouter } from "components/Router"
import { InlineList } from "components/InlineList"
import { Description } from "components/Description"
import { getPersonalInformations } from "domains/user/model"

const NavigationTypes = {
  LEISURE_SAILING: 1,
  REGATTA_SAILING: 2,
  MOTORBOAT: 3,
  SEA_FISHING: 4,
}

export function AccountProfileShowPage({
  user,
  tabs = window.accountNav,
  infos,
}) {
  const translator = useTranslator()

  const navInterest =
    user.navigationInterestsCrew && user.type.enum_value === UserType.CREW
      ? user.navigationInterestsCrew
      : user.navigationInterestsOwner && user.type.enum_value === UserType.OWNER
        ? user.navigationInterestsOwner
        : null

  const practicesLeisureSailing = user.navigationTypes.find(
    (navigationType) =>
      navigationType.id === NavigationTypes.LEISURE_SAILING ||
      navigationType.id === NavigationTypes.MOTORBOAT,
  )

  const practicesRegattaSailing = user.navigationTypes.find(
    (navigationType) => navigationType.id === NavigationTypes.REGATTA_SAILING,
  )

  const practicesSeaFishing = user.navigationTypes.find(
    (navigationType) => navigationType.id === NavigationTypes.SEA_FISHING,
  )

  const connectedUser = useConnectedUser()
  const router = useRouter()

  const isOwnProfile = connectedUser?.id === user.id

  const extraActions = [
    isOwnProfile ? (
      <Button
        href={router.generate("app_user_account_edit")}
        iconLeft={<Pen className="w-4" />}
        variant="outline"
        color="secondary"
        key="edit-profile"
      >
        {translator.trans(
          "account.profile.show.updateProfileCTA",
          null,
          "pages",
        )}
      </Button>
    ) : null,
  ].filter(Boolean)

  const personalInformations = getPersonalInformations(user)
  const shouldShowPersonalInformations =
    Object.keys(personalInformations).length > 0

  return (
    <div>
      <ProfilePage
        user={user}
        tabs={tabs}
        infos={infos}
        extraActions={extraActions}
      >
        {user.about ? (
          <section
            className="px-5 bg-white py-4 space-y-3"
            aria-labelledby={"about-title"}
          >
            <Text variant="headline5" tag="h2" id="about-title">
              {translator.trans("account.profile.show.about", null, "pages")}
            </Text>
            <Description>{user.about}</Description>
          </section>
        ) : null}
        {user.navigationTypes ? (
          <div>
            <Separator />
            <section aria-labelledby="navigation" className="py-8 space-y-6">
              <Text variant="headline5" tag="h2" id="navigation">
                {translator.trans(
                  "account.profile.show.navigationTypesTitle",
                  null,
                  "pages",
                )}
              </Text>
              <div className="space-y-6">
                <section
                  aria-labelledby="navigation-practice-1"
                  className="space-y-2"
                >
                  <Text variant="subtitle2" tag="h3" id="navigation-practice-1">
                    {translator.trans(
                      "account.profile.show.practicing",
                      null,
                      "pages",
                    )}
                  </Text>
                  {user.navigationTypes?.length > 0 ? (
                    <InlineList separator={null}>
                      {user.navigationTypes.map((navigationType) => {
                        return (
                          <InlineList.Item key={navigationType.id}>
                            <Tag
                              color="notice"
                              key={navigationType.id}
                              aria-label={navigationType.name}
                              label={navigationType.name}
                              iconStart={
                                navigationType.icon ? (
                                  <DynamicIcon
                                    name={navigationType.icon}
                                    className="w-3.5"
                                  />
                                ) : null
                              }
                            />
                          </InlineList.Item>
                        )
                      })}
                    </InlineList>
                  ) : null}

                  {user.navigationStyles?.length > 0 ? (
                    <InlineList separator={null}>
                      {user.navigationStyles?.map((navigationStyle) => {
                        return (
                          <InlineList.Item key={navigationStyle.id}>
                            <Tag
                              color="notice"
                              aria-label={navigationStyle.name}
                              label={navigationStyle.name}
                            />
                          </InlineList.Item>
                        )
                      })}
                    </InlineList>
                  ) : null}
                </section>
                <section
                  aria-labelledby="navigation-practice-2"
                  className="space-y-2"
                >
                  <Text variant="subtitle2" tag="h3" id="navigation-practice-2">
                    {translator.trans(
                      "account.profile.show.status",
                      null,
                      "pages",
                    )}
                  </Text>
                  <Tag color="notice" label={user.type.enum_label} />
                  {user.organizationType ? (
                    <Tag
                      color="notice"
                      label={user.organizationType.enum_label}
                    />
                  ) : null}
                </section>
                {user.navigationAvailabilities?.length > 0 ? (
                  <section
                    aria-labelledby="navigation-practice-3"
                    className="space-y-2"
                  >
                    <Text
                      variant="subtitle2"
                      tag="h3"
                      id="navigation-practice-3"
                    >
                      {translator.trans(
                        "account.profile.show.availability",
                        null,
                        "pages",
                      )}
                    </Text>
                    <InlineList separator={null}>
                      {user.navigationAvailabilities.map(
                        (navigationAbility) => {
                          return (
                            <InlineList.Item key={navigationAbility.id}>
                              <Tag
                                color="notice"
                                aria-label={navigationAbility.name}
                                label={navigationAbility.name}
                              />
                            </InlineList.Item>
                          )
                        },
                      )}
                    </InlineList>
                  </section>
                ) : null}

                {user.civility.enum_value !== Civility.ORGANIZATION &&
                user.websiteUses?.length > 0 ? (
                  <section
                    aria-labelledby="navigation-practice-4"
                    className="space-y-2"
                  >
                    <Text
                      variant="subtitle2"
                      tag="h3"
                      id="navigation-practice-4"
                    >
                      {translator.trans(
                        "account.profile.show.pairing",
                        null,
                        "pages",
                      )}
                    </Text>

                    <InlineList separator={null}>
                      {user.websiteUses.map((websiteUse) => {
                        return (
                          <InlineList.Item key={websiteUse.enum_value}>
                            <Tag
                              color="notice"
                              aria-label={websiteUse.enum_label}
                              label={websiteUse.enum_label}
                            />
                          </InlineList.Item>
                        )
                      })}
                    </InlineList>
                  </section>
                ) : null}
              </div>
            </section>
            <Separator />
            <section
              aria-labelledby="personal-infos"
              className="space-y-4 py-8"
            >
              <Text variant="headline5" tag="h2" id="personal-infos">
                {translator.trans(
                  "account.profile.show.personalInfos",
                  null,
                  "pages",
                )}
              </Text>

              {shouldShowPersonalInformations ? (
                <div className="space-y-4 lg:space-y-6">
                  <InlineList separator={null}>
                    {personalInformations.age ? (
                      <InlineList.Item>
                        <Tag
                          color="default"
                          aria-label={translator.trans(
                            "account.profile.show.age",
                            null,
                            "pages",
                          )}
                          label={`${
                            personalInformations.age
                          } ${translator.trans(
                            "account.profile.show.yearsOld",
                            null,
                            "pages",
                          )}`}
                        />
                      </InlineList.Item>
                    ) : null}
                    {personalInformations.maritalStatus ? (
                      <InlineList.Item>
                        <Tag
                          color="default"
                          aria-label={translator.trans(
                            "account.profile.show.maritalStatus",
                            null,
                            "pages",
                          )}
                          label={user.maritalStatus.enum_label}
                        />
                      </InlineList.Item>
                    ) : null}
                    {personalInformations.nbChildren ? (
                      <InlineList.Item>
                        <Tag
                          color="default"
                          aria-label={translator.trans(
                            "account.profile.show.nbChildren",
                            null,
                            "pages",
                          )}
                          label={`${user.nbChildren} ${translator.trans(
                            "account.profile.show.children",
                            null,
                            "pages",
                          )}`}
                        />
                      </InlineList.Item>
                    ) : null}
                  </InlineList>

                  {personalInformations.languages &&
                  personalInformations.languages.length > 0 ? (
                    <div>
                      <Text
                        variant="subtitle2"
                        tag="h3"
                        className="text-light"
                        id="personal-infos-1"
                      >
                        {translator.trans(
                          "account.profile.show.spokenLanguages",
                          null,
                          "pages",
                        )}
                      </Text>
                      <ul
                        aria-labelledby={translator.trans(
                          "account.profile.show.spokenLanguages",
                          null,
                          "pages",
                        )}
                      >
                        {personalInformations.languages.map(
                          (language, index) => (
                            <Text
                              key={language.id}
                              tag="li"
                              variant="body2"
                              aria-label={language.lang.name}
                              className="inline-block mr-1"
                            >
                              {`${
                                language.lang.name
                              } (${language.speakingLevel.enum_label.toLowerCase()})`}
                              {index !== user.languages.length - 1
                                ? ", "
                                : null}
                            </Text>
                          ),
                        )}
                      </ul>
                    </div>
                  ) : null}

                  {personalInformations.job ? (
                    <div>
                      <Text
                        variant="subtitle2"
                        tag="h3"
                        className="text-light"
                        id="personal-infos-job"
                      >
                        {translator.trans(
                          "account.profile.show.job",
                          null,
                          "pages",
                        )}
                      </Text>
                      <Text
                        variant="body2"
                        aria-labelledby={"personal-infos-job"}
                      >
                        {personalInformations.job}
                      </Text>
                    </div>
                  ) : null}
                </div>
              ) : (
                <Text variant="body2" className="text-light">
                  {translator.trans(
                    "account.profile.show.noPersonalInfos",
                    null,
                    "pages",
                  )}
                </Text>
              )}
            </section>
            <Separator />
            {navInterest?.length > 0 || user.personalInterests?.length > 0 ? (
              <>
                <section
                  aria-labelledby={translator.trans(
                    "account.profile.show.goals",
                    null,
                    "pages",
                  )}
                  className="space-y-4 py-8"
                >
                  <Text variant="headline5">
                    {translator.trans(
                      "account.profile.show.goals",
                      null,
                      "pages",
                    )}
                  </Text>
                  <Text variant="body2">
                    {translator.trans(
                      "account.profile.show.lookingFor",
                      null,
                      "pages",
                    )}
                  </Text>
                  <NestableList>
                    {navInterest?.length > 0
                      ? navInterest.map((interest) => {
                          return (
                            <Text
                              tag="li"
                              variant="body2"
                              key={interest.enum_value}
                            >
                              {interest.enum_label}
                            </Text>
                          )
                        })
                      : null}
                    {user.personalInterests?.map((personalInterest) => {
                      return (
                        <Text
                          tag="li"
                          variant="body2"
                          key={personalInterest.enum_value}
                        >
                          {personalInterest.enum_label}
                        </Text>
                      )
                    })}
                  </NestableList>
                </section>
                <Separator />
              </>
            ) : null}
            <BudgetSection user={user} />
          </div>
        ) : null}
        <NavigationExperience user={user} />
        <Separator />
        <NavigationDiplomas user={user} />
        <Separator />
        {practicesLeisureSailing ? (
          <>
            <LeisureSailingProfile user={user} />
            <Separator />
          </>
        ) : null}
        {practicesRegattaSailing ? (
          <>
            <RegateProfile user={user} />
            <Separator />
          </>
        ) : null}
        {practicesSeaFishing ? (
          <>
            <SeaFishingProfile user={user} />
            <Separator />
          </>
        ) : null}
        <AboardProfile user={user} />
      </ProfilePage>
    </div>
  )
}

function AccordionSection({ label, children }) {
  return (
    <div className="space-y-6 py-5">
      <Accordion>
        <AccordionSummary
          expandedIcon={<MinusCircle className="w-4" />}
          collapsedIcon={<PlusCircle className="w-4" />}
        >
          <Text className="text-primary-dark" variant="headline5">
            {label}
          </Text>
        </AccordionSummary>
        <AccordionContent>
          {(expanded) => (expanded ? children : null)}
        </AccordionContent>
      </Accordion>
    </div>
  )
}

const LeisureSailingProfile = ({ user }) => {
  const translator = useTranslator()

  const hobbies = user.navigationHobbies?.map((hobby) => {
    if (hobby.enum_value !== NavigationHobby.OTHER) {
      return hobby
    }

    return {
      ...hobby,
      enum_label: user.navigationHobbiesOther,
    }
  })

  return (
    <AccordionSection
      label={translator.trans(
        "account.profile.show.crusadeProfile",
        null,
        "pages",
      )}
    >
      <DescriptionsList>
        <DescriptionItem
          label={translator.trans(
            "account.profile.show.navigationHobbies",
            null,
            "pages",
          )}
          value={hobbies || []}
        />
        <DescriptionItem
          label={translator.trans(
            "account.profile.show.navigationParticipation",
            null,
            "pages",
          )}
          value={user.navigationParticipation}
        />
        <DescriptionItem
          label={translator.trans(
            "account.profile.show.navigationComfort",
            null,
            "pages",
          )}
          value={user.navigationComfort}
        />
        <DescriptionItem
          label={translator.trans(
            "account.profile.show.navigationPrefNightPort",
            null,
            "pages",
          )}
          value={user.navigationPrefNightPort}
        />
        <DescriptionItem
          label={translator.trans(
            "account.profile.show.nagivationPrefNightAnchor",
            null,
            "pages",
          )}
          value={user.navigationPrefNightAnchor}
        />
        <DescriptionItem
          label={translator.trans(
            "account.profile.show.navigationPrefNightQuarter",
            null,
            "pages",
          )}
          value={user.navigationPrefNightQuarter}
        />
        <DescriptionItem
          label={translator.trans(
            "account.profile.show.navigationPrefRestaurant",
            null,
            "pages",
          )}
          value={user.navigationPrefRestaurant}
        />
        <DescriptionItem
          label={translator.trans(
            "account.profile.show.navigationPrefParty",
            null,
            "pages",
          )}
          value={user.navigationPrefParty}
        />
        <DescriptionItem
          label={translator.trans(
            "account.profile.show.navigationPrefShopping",
            null,
            "pages",
          )}
          value={user.navigationPrefShopping}
        />
        <DescriptionItem
          label={translator.trans(
            "account.profile.show.navigationPrefCulture",
            null,
            "pages",
          )}
          value={user.navigationPrefCulture}
        />
      </DescriptionsList>
    </AccordionSection>
  )
}

const RegateProfile = ({ user }) => {
  const translator = useTranslator()

  return (
    <AccordionSection
      label={translator.trans(
        "account.profile.show.regattaProfile",
        null,
        "pages",
      )}
    >
      <DescriptionsList>
        <DescriptionItem
          label={translator.trans("account.profile.show.height", null, "pages")}
          value={
            user.height?.value ? <ValueWithUnit value={user.height} /> : null
          }
        />
        <DescriptionItem
          label={translator.trans("account.profile.show.weight", null, "pages")}
          value={
            user.weight?.value ? <ValueWithUnit value={user.weight} /> : null
          }
        />
        <DescriptionItem
          label={translator.trans(
            "account.profile.show.regatta",
            null,
            "pages",
          )}
          value={user.regatta}
        />
        {!user.regatta ? (
          <DescriptionItem
            label={translator.trans(
              "account.profile.show.regattaNavigationXp",
              null,
              "pages",
            )}
            value={user.regattaNavigationXp}
          />
        ) : null}
        <DescriptionItem
          label={translator.trans(
            "account.profile.show.regattaWishes",
            null,
            "pages",
          )}
          value={
            user.type.enum_value === UserType.OWNER
              ? user.regattaOwnerWishes
              : user.regattaCrewWishes
          }
        />
        {user.regatta ? (
          <DescriptionItem
            label={translator.trans(
              "account.profile.show.regattaNavigationRecent",
              null,
              "pages",
            )}
            value={user.regattaNavigationRecent}
          />
        ) : null}
        {user.regatta ? (
          <DescriptionItem
            label={translator.trans(
              "account.profile.show.regattaNavigationNb",
              null,
              "pages",
            )}
            value={user.regattaNavigationNb}
          />
        ) : null}

        {user.regatta ? (
          <DescriptionItem
            label={translator.trans(
              "account.profile.show.regattaOldTypes",
              null,
              "pages",
            )}
            value={user.regattaOldTypes.map(
              ({ name, xpYear }) => `${name.enum_label} ${xpYear}`,
            )}
          />
        ) : null}

        {user.regatta ? (
          <DescriptionItem
            label={translator.trans(
              "account.profile.show.regattaTypes",
              null,
              "pages",
            )}
            value={user.regattaTypes}
          />
        ) : null}

        {user.regatta ? (
          <DescriptionItem
            label={translator.trans(
              "account.profile.show.regattaPositions",
              null,
              "pages",
            )}
            value={user.regattaPositions}
          />
        ) : null}

        <DescriptionItem
          label={translator.trans(
            "account.profile.show.certificationLicenseFfv",
            null,
            "pages",
          )}
          value={
            user.certificationLicenseFfv ? (
              <Link
                href={`https://www.ffvoile.fr/ffv/sportif/ClmtCoureurFiche.asp?clid=${user.certificationLicenseFfv}`}
                external
              >
                {user.certificationLicenseFfv}
              </Link>
            ) : null
          }
        />

        <DescriptionItem
          label={translator.trans(
            "account.profile.show.regattaBoatClasses",
            null,
            "pages",
          )}
          value={user.regattaBoatClasses}
        />

        <DescriptionItem
          label={translator.trans(
            "account.profile.show.regattaBoatGauges",
            null,
            "pages",
          )}
          value={user.regattaBoatGauges}
        />
      </DescriptionsList>
    </AccordionSection>
  )
}

const SeaFishingProfile = ({ user }) => {
  const translator = useTranslator()

  return (
    <AccordionSection
      label={translator.trans(
        "account.profile.show.seaFishingProfile",
        null,
        "pages",
      )}
    >
      <DescriptionsList>
        <DescriptionItem
          label={translator.trans(
            "account.profile.show.fishingSeaTypes",
            null,
            "pages",
          )}
          value={user.fishingSeaTypes}
        />
        <DescriptionItem
          label={translator.trans(
            "account.profile.show.fishingWaterTypes",
            null,
            "pages",
          )}
          value={user.fishingWaterTypes}
        />
        <DescriptionItem
          label={translator.trans(
            "account.profile.show.certificationLicenseFfpm",
            null,
            "pages",
          )}
          value={user.certificationLicenseFfpm}
        />
      </DescriptionsList>
    </AccordionSection>
  )
}

const NavigationExperience = ({ user }) => {
  const translator = useTranslator()
  const [certifiedCertifications, notCertifiedCertifications] = partition(
    user.certificationLevels,
    (certificationLevel) => certificationLevel.certified,
  )

  return (
    <AccordionSection
      label={translator.trans(
        "account.profile.show.navigationExperience",
        null,
        "pages",
      )}
    >
      <div className="space-y-4">
        <DescriptionsList>
          <DescriptionItem
            label={translator.trans(
              "account.profile.show.navigationLevel",
              null,
              "pages",
            )}
            value={user.navigationLevel}
          />
          <DescriptionItem
            label={translator.trans(
              "account.profile.show.navigationSpecificity",
              null,
              "pages",
            )}
            value={user.navigationSpecificity}
          />
          <DescriptionItem
            label={translator.trans(
              "account.profile.show.navigationDateSince",
              null,
              "pages",
            )}
            value={user.navigationDateSince}
          />
          <DescriptionItem
            label={translator.trans(
              "account.profile.show.navigationDateLast",
              null,
              "pages",
            )}
            value={user.navigationDateLast}
          />
          <DescriptionItem
            label={translator.trans(
              "account.profile.show.xpNbMiles",
              null,
              "pages",
            )}
            value={user.xpNbMiles}
          />
          <DescriptionItem
            label={translator.trans(
              "account.profile.show.xpNavigationTime",
              null,
              "pages",
            )}
            value={user.xpNavigationTime}
          />
          <DescriptionItem
            label={translator.trans(
              "account.profile.show.xpNavigationNight",
              null,
              "pages",
            )}
            value={user.xpNavigationNight}
          />
          <DescriptionItem
            label={translator.trans(
              "account.profile.show.xpNavigationPositioning",
              null,
              "pages",
            )}
            value={user.xpNavigationPositioning}
          />
        </DescriptionsList>

        <div className="space-y-4">
          {certifiedCertifications.length > 0 ||
          notCertifiedCertifications.length > 0 ? (
            <Text className="text-default" variant="headline5">
              {translator.trans(
                "account.profile.show.certificationTitle",
                null,
                "pages",
              )}
            </Text>
          ) : null}
          <DescriptionsList>
            {certifiedCertifications.length > 0 ? (
              <DescriptionItem
                label={
                  <div>
                    <span>
                      {translator.trans(
                        "account.profile.show.certificationLevels",
                        null,
                        "pages",
                      )}
                    </span>
                    <RawLink
                      href={translator.trans(
                        "account.profile.show.certificationLevelsHelpUrl",
                        null,
                        "pages",
                      )}
                      external
                    >
                      <QuestionCircle className="w-4 text-primary-default" />
                    </RawLink>
                  </div>
                }
                value={
                  <ul className="space-y-2">
                    {certifiedCertifications.map((certification) => {
                      return (
                        <Text
                          variant="body2"
                          tag="li"
                          key={certification.id}
                          className="flex space-x-1 lg:space-x-2"
                        >
                          {certification.certificationLevel.type.enum_value ===
                          CertificationLevelType.FFV ? (
                            <FFVLevelIcon
                              level={certification.certificationLevel.level}
                              className="lg:w-10 w-6"
                            />
                          ) : null}
                          <span>
                            {certification.certificationLevel.type
                              .enum_value === CertificationLevelType.CANADA
                              ? "Voile Canada".toUpperCase()
                              : null}{" "}
                            {certification.certificationLevel.name}{" "}
                            {certification.validated
                              ? translator.trans(
                                  "account.profile.show.certificationValidated",
                                  null,
                                  "pages",
                                )
                              : null}
                          </span>
                        </Text>
                      )
                    })}
                  </ul>
                }
              />
            ) : null}
            {notCertifiedCertifications.length > 0 ? (
              <DescriptionItem
                label={
                  <div>
                    <span>
                      {translator.trans(
                        "account.profile.show.certificationNotValidatedLevels",
                        null,
                        "pages",
                      )}
                    </span>
                    <RawLink
                      href={translator.trans(
                        "account.profile.show.certificationNotValidatedLevelsHelpUrl",
                        null,
                        "pages",
                      )}
                      external
                    >
                      <QuestionCircle className="w-4 text-primary-default" />
                    </RawLink>
                  </div>
                }
                value={
                  <ul className="space-y-2">
                    {notCertifiedCertifications.map((notCertifiedDiploma) => {
                      return (
                        <Text
                          variant="body2"
                          tag="li"
                          key={notCertifiedDiploma.id}
                          className="flex space-x-1 lg:space-x-2"
                        >
                          <span>
                            {notCertifiedDiploma.certificationLevel.type
                              .enum_value === CertificationLevelType.CANADA
                              ? translator
                                  .trans(
                                    "account.profile.show.certificationLevelCanadaTitle",
                                    null,
                                    "pages",
                                  )
                                  .toUpperCase()
                              : notCertifiedDiploma.certificationLevel.type
                                    .enum_value ===
                                  CertificationLevelType.RYA_MCA
                                ? null
                                : notCertifiedDiploma.certificationLevel.type.enum_value.toUpperCase()}{" "}
                            {notCertifiedDiploma.certificationLevel.name}{" "}
                            {notCertifiedDiploma.validated
                              ? translator.trans(
                                  "account.profile.show.certificationValidated",
                                  null,
                                  "pages",
                                )
                              : null}
                          </span>
                        </Text>
                      )
                    })}
                  </ul>
                }
              />
            ) : null}
          </DescriptionsList>
        </div>
      </div>
    </AccordionSection>
  )
}

const NavigationDiplomas = ({ user }) => {
  const translator = useTranslator()

  const frenchDegrees = [
    ...ensureArray(user.certificationLicensePleasure),
    ...ensureArray(user.certificationLicenseTeaching),
    ...ensureArray(user.certificationLicenseNavy),
  ]

  const showFr = user.certificationLicense && frenchDegrees.length > 0
  const showBe = user.certificationLicense && user.certificationLicenseBe
  const showCh = user.certificationLicense && user.certificationLicenseCh
  const showCa = user.certificationLicense && user.certificationLicenseCa
  const showQb = user.certificationLicense && user.certificationLicenseQb
  const showGb = user.certificationLicense && user.certificationLicenseGb
  const showEu = user.certificationLicense && user.certificationLicenseEu
  const showDe = user.certificationLicense && user.certificationLicenseDe
  const showCoastGuard =
    user.certificationLicense && user.certificationLicenseCoastGuard
  const showUs = user.certificationLicense && user.certificationLicenseUs
  const showSailing =
    user.certificationLicense && user.certificationLicenseSailing
  const showBoating =
    user.certificationLicense && user.certificationLicenseBoating

  return (
    <AccordionSection
      label={translator.trans(
        "account.profile.show.navigationDiplomas",
        null,
        "pages",
      )}
    >
      <DescriptionsList>
        {!showFr &&
        !showBe &&
        !showCh &&
        !showCa &&
        !showQb &&
        !showGb &&
        !showEu &&
        !showDe &&
        !showCoastGuard &&
        !showUs &&
        !showSailing &&
        !showBoating ? (
          <Text>
            {translator.trans("account.profile.show.noDiplomas", null, "pages")}
          </Text>
        ) : null}
        {showFr ? (
          <DescriptionItem
            label={translator.trans(
              "account.profile.show.certificationLicenseFr",
              null,
              "pages",
            )}
            value={user.certificationLicense ? frenchDegrees : null}
          />
        ) : null}
        {showBe ? (
          <DescriptionItem
            label={translator.trans(
              "account.profile.show.certificationLicenseBe",
              null,
              "pages",
            )}
            value={
              user.certificationLicense ? user.certificationLicenseBe : null
            }
          />
        ) : null}
        {showCh ? (
          <DescriptionItem
            label={translator.trans(
              "account.profile.show.certificationLicenseCh",
              null,
              "pages",
            )}
            value={
              user.certificationLicense ? user.certificationLicenseCh : null
            }
          />
        ) : null}
        {showCa ? (
          <DescriptionItem
            label={translator.trans(
              "account.profile.show.certificationLicenseCa",
              null,
              "pages",
            )}
            value={
              user.certificationLicense ? user.certificationLicenseCa : null
            }
          />
        ) : null}
        {showQb ? (
          <DescriptionItem
            label={translator.trans(
              "account.profile.show.certificationLicenseQb",
              null,
              "pages",
            )}
            value={
              user.certificationLicense ? user.certificationLicenseQb : null
            }
          />
        ) : null}
        {showGb ? (
          <DescriptionItem
            label={translator.trans(
              "account.profile.show.certificationLicenseGb",
              null,
              "pages",
            )}
            value={
              user.certificationLicense ? user.certificationLicenseGb : null
            }
          />
        ) : null}
        {showEu ? (
          <DescriptionItem
            label={translator.trans(
              "account.profile.show.certificationLicenseEu",
              null,
              "pages",
            )}
            value={
              user.certificationLicense ? user.certificationLicenseEu : null
            }
          />
        ) : null}
        {showDe ? (
          <DescriptionItem
            label={translator.trans(
              "account.profile.show.certificationLicenseDe",
              null,
              "pages",
            )}
            value={
              user.certificationLicense ? user.certificationLicenseDe : null
            }
          />
        ) : null}
        {showCoastGuard ? (
          <DescriptionItem
            label={translator.trans(
              "account.profile.show.certificationLicenseCoastGuard",
              null,
              "pages",
            )}
            value={
              user.certificationLicense
                ? user.certificationLicenseCoastGuard
                : null
            }
          />
        ) : null}
        {showUs ? (
          <DescriptionItem
            label={translator.trans(
              "account.profile.show.certificationLicenseUs",
              null,
              "pages",
            )}
            value={
              user.certificationLicense ? user.certificationLicenseUs : null
            }
          />
        ) : null}
        {showSailing ? (
          <DescriptionItem
            label={translator.trans(
              "account.profile.show.certificationLicenseUsSailing",
              null,
              "pages",
            )}
            value={
              user.certificationLicense
                ? user.certificationLicenseSailing
                : null
            }
          />
        ) : null}
        {showBoating ? (
          <DescriptionItem
            label={translator.trans(
              "account.profile.show.certificationLicenseUsBoating",
              null,
              "pages",
            )}
            value={
              user.certificationLicense
                ? user.certificationLicenseBoating
                : null
            }
          />
        ) : null}
      </DescriptionsList>
    </AccordionSection>
  )
}

const ensureArray = (maybeArray) => {
  return Array.isArray(maybeArray) ? maybeArray : []
}

const AboardProfile = ({ user }) => {
  const translator = useTranslator()

  return (
    <AccordionSection
      label={translator.trans(
        "account.profile.show.aboardProfile",
        null,
        "pages",
      )}
    >
      <DescriptionsList>
        <DescriptionItem
          label={translator.trans(
            "account.profile.show.aboardFood",
            null,
            "pages",
          )}
          value={user.aboardFood}
        />
        <DescriptionItem
          label={translator.trans(
            "account.profile.show.aboardNaturist",
            null,
            "pages",
          )}
          value={user.aboardNaturist}
        />
        <DescriptionItem
          label={translator.trans(
            "account.profile.show.aboardGreen",
            null,
            "pages",
          )}
          value={user.aboardGreen}
        />
        <DescriptionItem
          label={translator.trans(
            "account.profile.show.aboardSmoke",
            null,
            "pages",
          )}
          value={user.aboardSmoke}
        />
        <DescriptionItem
          label={translator.trans(
            "account.profile.show.aboardSafetyJacket",
            null,
            "pages",
          )}
          value={user.aboardSafetyJacket}
        />
        <DescriptionItem
          label={translator.trans(
            "account.profile.show.aboardSafetyProcedure",
            null,
            "pages",
          )}
          value={user.aboardSafetyProcedure}
        />
        <DescriptionItem
          label={translator.trans(
            "account.profile.show.aboardMoodMine",
            null,
            "pages",
          )}
          value={user.aboardMoodMine}
        />
        <DescriptionItem
          label={translator.trans(
            "account.profile.show.aboardOpinion",
            null,
            "pages",
          )}
          value={user.aboardOpinion}
        />
        <DescriptionItem
          label={translator.trans(
            "account.profile.show.aboardMoodOthers",
            null,
            "pages",
          )}
          value={user.aboardMoodOthers}
        />
        <DescriptionItem
          label={translator.trans(
            "account.profile.show.aboardSkills",
            null,
            "pages",
          )}
          value={user.aboardSkills}
        />
        {user.aboardSkillsOther ? (
          <DescriptionItem
            label={translator.trans(
              "account.profile.show.aboardSkillsOther",
              null,
              "pages",
            )}
            value={user.aboardSkillsOther}
          />
        ) : null}
      </DescriptionsList>
    </AccordionSection>
  )
}

const BudgetSection = ({ user }) => {
  const translator = useTranslator()

  const userTypeToComponent = {
    [UserType.CREW]: BudgetCrewSection,
    [UserType.OWNER]: BudgetOwnerSection,
  }

  const Component = userTypeToComponent[user.type.enum_value]

  if (!Component) {
    return null
  }

  return (
    <>
      <section
        aria-labelledby={translator.trans(
          "account.profile.show.budget",
          null,
          "pages",
        )}
        className="space-y-4 py-8"
      >
        <Text variant="headline5" id="budget">
          {translator.trans("account.profile.show.budget", null, "pages")}
        </Text>
        <Component user={user} />
      </section>
      <Separator />
    </>
  )
}

const BudgetCrewSection = ({ user }) => {
  const budget = user.budgetCrew

  return (
    <InlineList separator={null}>
      {budget.choice ? (
        <InlineList.Item>
          <Tag label={budget.choice.enum_label} />
        </InlineList.Item>
      ) : null}
      {budget.sharing?.map((item) => (
        <InlineList.Item key={item.enum_value}>
          <Tag label={item.enum_label} />
        </InlineList.Item>
      ))}
      {budget.other?.map((item) => (
        <InlineList.Item key={item.enum_value}>
          <Tag key={item.enum_value} label={item.enum_label} />
        </InlineList.Item>
      ))}
    </InlineList>
  )
}

const BudgetOwnerSection = ({ user }) => {
  const translator = useTranslator()
  const budget = user.budgetOwner

  return (
    <InlineList separator={null}>
      {budget.choice ? (
        <InlineList.Item>
          <Tag label={budget.choice.enum_label} />
        </InlineList.Item>
      ) : null}
      {budget.sharing?.map((item) => (
        <InlineList.Item key={item.enum_value}>
          <BudgetOwnerSharingTag item={item} budget={budget} />
        </InlineList.Item>
      ))}
      {budget.pro.price?.amount !== null &&
      budget.pro.price?.amount > 0 &&
      budget.pro.price?.currency !== null &&
      budget.pro.priceMeasure !== null &&
      budget.pro.priceFrequency !== null ? (
        <InlineList.Item>
          <Tag
            label={translator.trans(
              "account.profile.show.pro.price",
              {
                price: formatPrice({
                  amount: budget.pro.price.amount,
                  currency: budget.pro.price.currency.enum_value,
                  locale: translator.locale,
                }),
                unit: `/${budget.pro.priceMeasure.enum_label}/${budget.pro.priceFrequency.enum_label}`,
              },
              "pages",
            )}
          />
        </InlineList.Item>
      ) : null}
      {budget.pro.pot?.amount !== null &&
      budget.pro.pot?.amount > 0 &&
      budget.pro.pot?.currency !== null ? (
        <InlineList.Item>
          <Tag
            label={translator.trans(
              "account.profile.show.sharingPot",
              {
                price: formatPrice({
                  amount: budget.pro.pot.amount,
                  currency: budget.pro.pot.currency.enum_value,
                  locale: translator.locale,
                }),
              },
              "pages",
            )}
          />
        </InlineList.Item>
      ) : null}
    </InlineList>
  )
}

const BudgetOwnerSharingTag = ({ item, budget }) => {
  let Component

  switch (item.enum_value) {
    case BudgetOwnerSharing.POT:
      Component = BudgetOwnerSharingPotTag
      break

    case BudgetOwnerSharing.MAINTENANCE:
      Component = BudgetOwnerSharingMaintenanceTag
      break

    default:
      Component = BudgetOwnerSharingGenericTag
  }

  return <Component item={item} budget={budget} />
}

const BudgetOwnerSharingPotTag = ({ budget }) => {
  const translator = useTranslator()

  if (
    budget.sharingPot?.amount !== null &&
    budget.sharingPot?.amount > 0 &&
    budget.sharingPot?.currency !== null
  ) {
    return (
      <Tag
        label={translator.trans(
          "account.profile.show.sharingPot",
          {
            price: formatPrice({
              amount: budget.sharingPot.amount,
              currency: budget.sharingPot.currency.enum_value,
              locale: translator.locale,
            }),
          },
          "pages",
        )}
      />
    )
  }

  return null
}

const BudgetOwnerSharingMaintenanceTag = ({ budget }) => {
  const translator = useTranslator()

  if (
    budget.sharingMaintenance?.amount !== null &&
    budget.sharingMaintenance?.amount > 0 &&
    budget.sharingMaintenance?.currency !== null
  ) {
    return (
      <Tag
        label={translator.trans(
          "account.profile.show.sharingMaintenance",
          {
            price: formatPrice({
              amount: budget.sharingMaintenance.amount,
              currency: budget.sharingMaintenance.currency.enum_value,
              locale: translator.locale,
            }),
          },
          "pages",
        )}
      />
    )
  }
}

const BudgetOwnerSharingGenericTag = ({ item }) => {
  return <Tag label={item.enum_label} />
}
